import {
  CenterModalPanel,
  SideSheetPanel,
  getViewportHeight,
  presenterKindForPlacement,
  type PanelPlacement,
  type PanelPresenter,
} from './panel-presenter'
import {
  SubmitIconSvg,
  applyPanelCornerRadius,
  applyShellInputAnimation,
  applyThemeVariables,
} from './sheet-shell-shared'
import {Shell, type ShellOptions} from './shell-base'
import {ShellStyles} from './shell-styles'
import type {AnagramMessage, CenterBarToDrawerConfig, EnvironmentConfig, ShellConfig} from './types'

declare const __BITS_CSS__: string
const BitsStyles: string = typeof __BITS_CSS__ !== 'undefined' ? __BITS_CSS__ : ''

declare const __ETS_CSS__: string
const EtsStyles: string = typeof __ETS_CSS__ !== 'undefined' ? __ETS_CSS__ : ''

export class CenterBarToDrawerShell extends Shell {
  private shellConfig: CenterBarToDrawerConfig
  private launcherHost: HTMLDivElement | null = null
  private drawerHost: HTMLDivElement | null = null
  private drawerRoot: HTMLDivElement | null = null
  private launcherRoot!: HTMLDivElement
  private launcherStack!: HTMLDivElement
  private inputLauncher!: HTMLDivElement
  private textInput!: HTMLInputElement
  private presenter!: PanelPresenter
  private currentIframe: HTMLIFrameElement | null = null
  private currentIframeContextKey: string | null = null
  private isOpen = false
  private isIframeReady = false
  private pendingSuggestion: string | null = null
  private pendingFocusInput = false
  private viewportResizeListener: (() => void) | null = null
  private scrollDepthRevealListener: (() => void) | null = null
  private hasMetScrollDepth = false

  constructor(
    config: EnvironmentConfig,
    experienceId: string,
    shellConfig: CenterBarToDrawerConfig,
    options?: ShellOptions
  ) {
    super(config, experienceId, options)
    this.shellConfig = shellConfig
  }

  mount(): void {
    const fontFamily = this.getShellFontStack(this.shellConfig.fontFamily)

    this.launcherHost = document.createElement('div')
    this.styleAsViewportOverlay(this.launcherHost)
    document.body.appendChild(this.launcherHost)

    const launcherShadow = this.launcherHost.attachShadow({mode: 'open'})
    this.attachFontStylesheet(launcherShadow, this.shellConfig.fontFamily)

    const launcherStyle = document.createElement('style')
    launcherStyle.textContent = BitsStyles
    launcherShadow.appendChild(launcherStyle)

    this.launcherRoot = document.createElement('div')
    this.launcherRoot.className = 'anagram-bits-root'
    this.launcherRoot.style.fontFamily = fontFamily
    this.launcherRoot.style.right = 'auto'
    this.launcherRoot.style.bottom = 'auto'
    launcherShadow.appendChild(this.launcherRoot)

    applyThemeVariables(this.launcherRoot, this.shellConfig.theme)
    this.syncLauncherViewportFrame()

    this.launcherStack = document.createElement('div')
    this.launcherStack.className = 'anagram-bits-stack'
    this.launcherRoot.appendChild(this.launcherStack)

    this.inputLauncher = document.createElement('div')
    this.inputLauncher.className = 'anagram-bits-launcher'
    applyShellInputAnimation(this.inputLauncher, this.shellConfig.animationStyle)

    if (this.shellConfig.showInputShadow) {
      const topShadow = document.createElement('div')
      topShadow.className = 'anagram-bits-crescent-shadow anagram-bits-crescent-shadow-top'
      this.inputLauncher.appendChild(topShadow)

      const bottomShadow = document.createElement('div')
      bottomShadow.className = 'anagram-bits-crescent-shadow anagram-bits-crescent-shadow-bottom'
      this.inputLauncher.appendChild(bottomShadow)
    }

    const form = document.createElement('form')
    form.className = 'anagram-bits-input'

    this.textInput = document.createElement('input')
    this.textInput.type = 'text'
    this.textInput.className = 'anagram-bits-input-field'
    this.textInput.placeholder = this.shellConfig.placeholderText

    const submitButton = document.createElement('button')
    submitButton.type = 'submit'
    submitButton.className = 'anagram-bits-input-icon'
    submitButton.innerHTML = SubmitIconSvg
    submitButton.setAttribute('aria-label', 'Submit')

    form.addEventListener('mousedown', (event) => {
      if (submitButton.contains(event.target as Node)) {
        return
      }

      if (!this.isOpen) {
        event.preventDefault()
        this.openSheet()
      }
    })

    form.addEventListener('submit', (event) => {
      event.preventDefault()
      const text = this.textInput.value.trim()
      if (text) {
        this.pendingSuggestion = text
        this.textInput.value = ''
      } else {
        this.pendingFocusInput = true
      }

      if (this.isOpen) {
        if (this.isIframeReady) {
          this.flushPendingActions()
        }
        return
      }

      this.openSheet()
    })

    form.appendChild(this.textInput)
    form.appendChild(submitButton)
    this.inputLauncher.appendChild(form)
    this.launcherStack.appendChild(this.inputLauncher)

    this.drawerHost = document.createElement('div')
    this.styleAsViewportOverlay(this.drawerHost)
    document.body.appendChild(this.drawerHost)

    const drawerShadow = this.drawerHost.attachShadow({mode: 'open'})
    this.attachFontStylesheet(drawerShadow, this.shellConfig.fontFamily)

    const drawerStyle = document.createElement('style')
    drawerStyle.textContent = BitsStyles + ShellStyles + EtsStyles
    drawerShadow.appendChild(drawerStyle)

    const drawerRoot = document.createElement('div')
    drawerRoot.className = 'anagram-root'
    drawerRoot.style.fontFamily = fontFamily
    applyPanelCornerRadius(drawerRoot, this.shellConfig.theme['--radius'])
    drawerShadow.appendChild(drawerRoot)
    this.drawerRoot = drawerRoot

    this.presenter = this.createPresenter(this.shellConfig.panelLocation)
    this.presenter.mount(drawerRoot)

    this.initializeScrollDepthReveal()
    this.viewportResizeListener = () => {
      this.syncLauncherViewportFrame()
      this.presenter.syncViewport()
      this.syncScrollDepthReveal()
    }
    window.addEventListener('resize', this.viewportResizeListener)
    window.visualViewport?.addEventListener('resize', this.viewportResizeListener, {passive: true})
    window.visualViewport?.addEventListener('scroll', this.viewportResizeListener, {passive: true})
    this.startListeningForIframeMessages()
  }

  override applyPreviewShellConfig(config: ShellConfig): boolean {
    if (config.formFactor !== 'center-bar-to-drawer' || !this.launcherHost) {
      return false
    }

    this.shellConfig = config as CenterBarToDrawerConfig
    this.updateActivePreviewConfig(config)
    if (this.drawerRoot) {
      applyPanelCornerRadius(this.drawerRoot, this.shellConfig.theme['--radius'])
    }
    applyThemeVariables(this.launcherRoot, this.shellConfig.theme)
    this.textInput.placeholder = this.shellConfig.placeholderText
    applyShellInputAnimation(this.inputLauncher, this.shellConfig.animationStyle)
    this.teardownScrollDepthRevealListener()
    this.initializeScrollDepthReveal()
    this.swapPresenterIfNeeded(this.shellConfig.panelLocation)
    this.presenter.applyPlacement(this.shellConfig.panelLocation)

    if (this.isOpen) {
      const {parentUrl, pageTitle} = this.getLiveIframePageContext()
      this.currentIframeContextKey = null
      this.ensureIframe(parentUrl, pageTitle)
    }

    return true
  }

  private createPresenter(placement: PanelPlacement): PanelPresenter {
    const callbacks = {
      onDismissRequested: () => this.closeSheet(),
      onClosed: () => {
        this.isOpen = false
        this.syncLauncherVisibility()
      },
    }

    if (presenterKindForPlacement(placement) === 'center-modal') {
      return new CenterModalPanel({
        fontFamily: this.getShellFontStack(this.shellConfig.fontFamily),
        theme: this.shellConfig.theme,
        callbacks,
      })
    }

    return new SideSheetPanel({
      fontFamily: this.getShellFontStack(this.shellConfig.fontFamily),
      placement,
      mobileDrawer: true,
      overlayDismiss: true,
      callbacks,
    })
  }

  private swapPresenterIfNeeded(placement: PanelPlacement): boolean {
    if (!this.drawerRoot || presenterKindForPlacement(placement) === this.presenter.kind) {
      return false
    }

    const wasOpen = this.isOpen
    this.presenter.destroy()
    this.currentIframe = null
    this.currentIframeContextKey = null
    this.isIframeReady = false
    this.isOpen = false

    this.presenter = this.createPresenter(placement)
    this.presenter.mount(this.drawerRoot)

    if (wasOpen) {
      this.openSheet()
    }

    return true
  }

  override destroy(): void {
    this.teardownDrawerListeners()
    this.presenter?.destroy()
    this.currentIframe?.remove()
    this.currentIframe = null
    this.currentIframeContextKey = null
    this.isOpen = false
    this.isIframeReady = false
    this.pendingSuggestion = null
    this.pendingFocusInput = false
    this.launcherHost?.remove()
    this.launcherHost = null
    this.drawerHost?.remove()
    this.drawerHost = null
    this.drawerRoot = null
    this.teardownScrollDepthRevealListener()

    super.destroy()
  }

  private teardownDrawerListeners(): void {
    if (this.viewportResizeListener) {
      window.removeEventListener('resize', this.viewportResizeListener)
      window.visualViewport?.removeEventListener('resize', this.viewportResizeListener)
      window.visualViewport?.removeEventListener('scroll', this.viewportResizeListener)
      this.viewportResizeListener = null
    }
  }

  protected override ownsIframeMessageSource(source: MessageEventSource): boolean {
    return source === this.currentIframe?.contentWindow
  }

  protected onIframeReady(): void {
    this.isIframeReady = true
    this.presenter.showReady()
    this.flushPendingActions()
  }

  protected handleShellSpecificIframeMessage(msg: AnagramMessage): void {
    switch (msg.type) {
      case 'ANAGRAM_RESIZE': {
        if (msg.experienceId !== this.experienceId) {
          break
        }

        const nextHeight = Number(msg.height)
        if (!Number.isNaN(nextHeight) && nextHeight > 0) {
          this.presenter.setBodyHeight(nextHeight)
        }
        break
      }

      case 'ANAGRAM_START_OVER': {
        if (msg.experienceId !== this.experienceId) {
          break
        }

        this.presenter.resetBodyHeight()
        break
      }

      case 'ANAGRAM_CLOSE_REQUESTED': {
        if (msg.experienceId !== this.experienceId) {
          break
        }

        this.closeSheet()
        break
      }
    }
  }

  private syncLauncherViewportFrame(): void {
    if (!this.launcherRoot) {
      return
    }

    const viewport = window.visualViewport
    const top = viewport?.offsetTop ?? 0
    const left = viewport?.offsetLeft ?? 0
    const width = viewport?.width ?? window.innerWidth
    const height = getViewportHeight()

    this.launcherRoot.style.top = `${top}px`
    this.launcherRoot.style.left = `${left}px`
    this.launcherRoot.style.width = `${width}px`
    this.launcherRoot.style.height = `${height}px`
  }

  private initializeScrollDepthReveal(): void {
    this.hasMetScrollDepth = this.isScrollDepthReached()
    this.syncLauncherVisibility()

    if (this.hasMetScrollDepth || this.scrollDepthRevealListener) {
      return
    }

    this.scrollDepthRevealListener = () => this.syncScrollDepthReveal()
    window.addEventListener('scroll', this.scrollDepthRevealListener, {passive: true})
  }

  private syncScrollDepthReveal(): void {
    if (this.hasMetScrollDepth || !this.isScrollDepthReached()) {
      return
    }

    this.hasMetScrollDepth = true
    this.teardownScrollDepthRevealListener()
    this.syncLauncherVisibility()
  }

  private syncLauncherVisibility(): void {
    if (!this.launcherRoot || this.isOpen) {
      return
    }

    this.launcherRoot.dataset.state = this.hasMetScrollDepth ? 'collapsed' : 'pending-scroll'
  }

  private teardownScrollDepthRevealListener(): void {
    if (!this.scrollDepthRevealListener) {
      return
    }

    window.removeEventListener('scroll', this.scrollDepthRevealListener)
    this.scrollDepthRevealListener = null
  }

  private isScrollDepthReached(): boolean {
    if (!this.shellConfig.revealAfterPageScroll) {
      return true
    }

    const scrollHeight = document.documentElement.scrollHeight
    const maxScroll = Math.max(0, scrollHeight - window.innerHeight)
    if (maxScroll === 0) {
      return true
    }

    return window.scrollY >= Math.min(window.innerHeight, maxScroll)
  }

  private openSheet(): void {
    if (this.isOpen) {
      return
    }

    this.isOpen = true
    this.launcherRoot.dataset.state = 'hidden'

    const {parentUrl, pageTitle} = this.getLiveIframePageContext()
    this.ensureIframe(parentUrl, pageTitle)

    if (this.isIframeReady) {
      this.presenter.showReady()
      this.flushPendingActions()
    } else {
      this.presenter.showLoading()
    }

    this.presenter.open()
  }

  private closeSheet(): void {
    if (!this.isOpen) {
      return
    }

    this.pendingSuggestion = null
    this.pendingFocusInput = false
    this.presenter.close()
  }

  private ensureIframe(parentUrl: string, pageTitle: string): void {
    const contextKey = this.buildIframeContextKey(parentUrl, pageTitle)
    if (this.currentIframe && this.currentIframeContextKey === contextKey) {
      return
    }

    this.currentIframe?.remove()

    const iframeUrl = this.createIframeUrl('sheet')
    iframeUrl.searchParams.append('parentUrl', parentUrl)
    if (pageTitle) {
      iframeUrl.searchParams.append('pageTitle', pageTitle)
    }

    this.currentIframe = this.createIframe(iframeUrl)
    this.currentIframeContextKey = contextKey
    this.isIframeReady = false
    this.presenter.showLoading()
    this.presenter.iframeContainer.appendChild(this.currentIframe)
  }

  private flushPendingActions(): void {
    if (this.pendingSuggestion && this.currentIframe?.contentWindow) {
      this.currentIframe.contentWindow.postMessage({type: 'ANAGRAM_AUTO_SUBMIT', text: this.pendingSuggestion}, '*')
    }

    if (this.pendingFocusInput && this.currentIframe?.contentWindow) {
      this.currentIframe.contentWindow.postMessage({type: 'ANAGRAM_FOCUS_INPUT'}, '*')
    }

    this.pendingSuggestion = null
    this.pendingFocusInput = false
  }
}
